import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatRial } from '../utils/formatMoneyFa';

const toPersianDigits = (s: string | number): string =>
  String(s).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

const MONTH_NAMES = [
  'فروردین',
  'اردیبهشت',
  'خرداد',
  'تیر',
  'مرداد',
  'شهریور',
  'مهر',
  'آبان',
  'آذر',
  'دی',
  'بهمن',
  'اسفند',
];

export interface WatchdogDayStats {
  count: number;
  naghdi: number;
  bime: number;
  total_amount: number;
}

export interface WatchdogTooltipState {
  kind?: 'day' | 'month' | 'year';
  x: number;
  y: number;
  date: string;
  title?: string;
  subtitle?: string;
  stats: WatchdogDayStats;
  isToday?: boolean;
  isCurrentMonth?: boolean;
  isCurrentYear?: boolean;
  peakCount?: number;
  intensityLabel?: string;
}

interface Props {
  tip: WatchdogTooltipState | null;
}

const formatDateTitle = (date: string): string => {
  const parts = date.split('/');
  if (parts.length !== 3) return toPersianDigits(date);
  const m = Number(parts[1]);
  return `${toPersianDigits(Number(parts[2]))} ${MONTH_NAMES[m - 1] || ''}`;
};

const WatchdogDayTooltip: React.FC<Props> = ({ tip }) => {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState({ left: 0, top: 0 });

  useLayoutEffect(() => {
    if (!tip) return;
    const card = cardRef.current;
    const pad = 12;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = card?.offsetWidth ?? 240;
    const h = card?.offsetHeight ?? 160;
    let left = tip.x - w / 2;
    let top = tip.y - h - 14;
    left = Math.max(pad, Math.min(left, vw - w - pad));
    if (top < pad) top = tip.y + 18;
    if (top + h > vh - pad) top = Math.max(pad, tip.y - h - 14);
    setPos({ left, top });
  }, [tip]);

  if (!tip) return null;

  const {
    stats,
    date,
    kind = 'day',
    title,
    subtitle,
    isToday,
    isCurrentMonth,
    isCurrentYear,
    peakCount = 1,
    intensityLabel,
  } = tip;
  const { count, naghdi, bime, total_amount: totalAmount } = stats;
  const intensity = count > 0 ? Math.min(100, Math.round((count / Math.max(1, peakCount)) * 100)) : 0;
  const cashPct = totalAmount > 0 ? (naghdi / totalAmount) * 100 : 0;
  const insPct = totalAmount > 0 ? (bime / totalAmount) * 100 : 0;
  const hasMoney = totalAmount > 0;
  const highlighted =
    kind === 'year' ? isCurrentYear : kind === 'month' ? isCurrentMonth : isToday;
  const displayTitle =
    title ||
    (kind === 'day' ? formatDateTitle(date) : toPersianDigits(date.replace(/\/.*/, '') || date));
  const displaySubtitle =
    subtitle ?? (kind === 'day' ? toPersianDigits(date) : undefined);
  const detailLabel =
    kind === 'year' ? 'جزئیات سال' : kind === 'month' ? 'جزئیات ماه' : 'جزئیات روز';
  const intensityHint =
    intensityLabel ||
    (peakCount > 0 && count === peakCount
      ? kind === 'year'
        ? 'پرترافیک‌ترین سال'
        : kind === 'month'
          ? 'شلوغ‌ترین ماه سال'
          : 'شلوغ‌ترین روز سال'
      : `${toPersianDigits(intensity)}٪ از اوج`);
  const highlightBadge =
    kind === 'year' ? 'امسال' : kind === 'month' ? 'ماه جاری' : 'امروز';

  return createPortal(
    <div
      ref={cardRef}
      className="pointer-events-none fixed z-[80] w-[17.5rem] overflow-hidden rounded-2xl border border-white/10 shadow-2xl shadow-indigo-950/40"
      style={{
        left: pos.left,
        top: pos.top,
        direction: 'rtl',
        animation: 'watchdogTipIn 180ms ease-out',
      }}
    >
      <style>{`
        @keyframes watchdogTipIn {
          from { opacity: 0; transform: translateY(6px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      <div
        className={`relative bg-gradient-to-br ${
          highlighted
            ? 'from-emerald-950 via-slate-900 to-indigo-950'
            : 'from-slate-900 via-indigo-950 to-violet-950'
        } px-4 py-3.5 text-white`}
      >
        <div
          className="pointer-events-none absolute -left-8 -top-8 h-24 w-24 rounded-full bg-indigo-400/20 blur-2xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-6 -right-6 h-20 w-20 rounded-full bg-amber-400/10 blur-2xl"
          aria-hidden
        />

        <div className="relative flex items-start justify-between gap-2">
          <div>
            <p className="text-[10px] font-medium text-indigo-200/80">{detailLabel}</p>
            <p className="mt-0.5 text-base font-bold tracking-tight">{displayTitle}</p>
            {displaySubtitle ? (
              <p className="mt-0.5 text-[10px] tabular-nums text-slate-400">{displaySubtitle}</p>
            ) : null}
          </div>
          {highlighted ? (
            <span className="shrink-0 rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] font-semibold text-emerald-300 ring-1 ring-emerald-400/30">
              {highlightBadge}
            </span>
          ) : null}
        </div>

        {count <= 0 ? (
          <div className="relative mt-4 rounded-xl border border-dashed border-white/15 bg-white/5 px-3 py-4 text-center">
            <p className="text-sm font-medium text-slate-200">بدون پذیرش</p>
            <p className="mt-1 text-[11px] text-slate-400">
              {kind === 'year' ? 'برای این سال داده‌ای ثبت نشده' : 'برای این روز داده‌ای ثبت نشده'}
            </p>
          </div>
        ) : (
          <>
            <div className="relative mt-4 flex items-center gap-3">
              <div className="relative flex h-14 w-14 shrink-0 items-center justify-center">
                <svg className="absolute inset-0 h-full w-full -rotate-90" viewBox="0 0 36 36">
                  <circle
                    cx="18"
                    cy="18"
                    r="15"
                    fill="none"
                    stroke="rgba(255,255,255,0.08)"
                    strokeWidth="3"
                  />
                  <circle
                    cx="18"
                    cy="18"
                    r="15"
                    fill="none"
                    stroke={highlighted ? '#34d399' : '#818cf8'}
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeDasharray={`${(intensity / 100) * 94.2} 94.2`}
                  />
                </svg>
                <span className="text-lg font-bold tabular-nums">{toPersianDigits(count)}</span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-slate-100">پذیرش</p>
                <p className="mt-0.5 text-[10px] text-slate-400">{intensityHint}</p>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/10">
                  <div
                    className={`h-full rounded-full ${
                      highlighted
                        ? 'bg-gradient-to-l from-emerald-400 to-emerald-300'
                        : 'bg-gradient-to-l from-indigo-400 to-violet-400'
                    }`}
                    style={{ width: `${Math.max(4, intensity)}%` }}
                  />
                </div>
              </div>
            </div>

            {hasMoney ? (
              <div className="relative mt-4 space-y-2.5 rounded-xl border border-white/10 bg-white/5 p-3">
                <div className="flex items-center justify-between text-[10px] text-slate-300">
                  <span>ترکیب درآمد</span>
                  <span className="tabular-nums text-slate-400">
                    {toPersianDigits(cashPct.toFixed(0))}٪ / {toPersianDigits(insPct.toFixed(0))}٪
                  </span>
                </div>
                <div className="flex h-2 overflow-hidden rounded-full bg-white/10">
                  <div
                    className="bg-gradient-to-l from-amber-400 to-amber-300"
                    style={{ width: `${Math.max(3, cashPct)}%` }}
                    title="نقدی"
                  />
                  <div
                    className="bg-gradient-to-l from-sky-400 to-cyan-300"
                    style={{ width: `${Math.max(3, insPct)}%` }}
                    title="بیمه"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-lg bg-amber-500/10 px-2.5 py-2 ring-1 ring-amber-400/20">
                    <p className="text-[9px] text-amber-200/80">نقدی</p>
                    <p className="mt-0.5 text-[11px] font-bold tabular-nums text-amber-100">
                      {formatRial(naghdi)}
                    </p>
                  </div>
                  <div className="rounded-lg bg-sky-500/10 px-2.5 py-2 ring-1 ring-sky-400/20">
                    <p className="text-[9px] text-sky-200/80">بیمه</p>
                    <p className="mt-0.5 text-[11px] font-bold tabular-nums text-sky-100">
                      {formatRial(bime)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between border-t border-white/10 pt-2 text-[10px]">
                  <span className="text-slate-400">جمع کل</span>
                  <span className="font-bold tabular-nums text-white">{formatRial(totalAmount)}</span>
                </div>
              </div>
            ) : (
              <p className="relative mt-3 text-center text-[10px] text-slate-500">
                مبلغ مالی برای این روز ثبت نشده
              </p>
            )}
          </>
        )}

        <span
          className="absolute left-1/2 top-full h-0 w-0 -translate-x-1/2 border-x-[7px] border-t-[7px] border-x-transparent border-t-slate-900/95"
          aria-hidden
        />
      </div>
    </div>,
    document.body
  );
};

export default WatchdogDayTooltip;
